import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { GetJson, PutJson, PostJson, DeleteJson } from '@awp/helpers/fetchReact';
import { REST_API, Views } from '@awp/constants';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FORM_MODE, Permissions } from '@awp/constants';
import * as Yup from 'yup';

import { getAvalancheGeographicAreas } from '@awp/stores/avalancheSlice';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import { selectHazardTechnicians } from '@awp/stores/dropdownsSlice';
import {
  selectAllHazardForecastDropDown,
  selectAllHazardForecastLevelDropDown,
  isEditing,
  currentAnalysis,
  setAnalysis,
  currentWeatherForecast,
  currentWeatherObservation,
  setWeatherObservation,
  currentWeatherForecastExternalObjects,
  setWeatherForecastExternalObjects,
} from '@awp/stores/hazardForecastSlice';
import { Row, Col, Card, Button, Spinner, Container, Accordion, OverlayTrigger, Tooltip } from 'react-bootstrap';
import CardHeaderToggle from '@components/Common/Accordion/CardHeaderToggle';
import { format as formatDate } from 'date-fns';

import {
  FormInput,
  FormCheckbox,
  FormTextArea,
  FormSelect,
  FormDatePicker,
  FormTreeSelect,
  FormTextReadOnly,
  FormForecastLevel,
  FormRadio,
  FormRadioGroup,
} from '@components/Common/AwpFormInputs';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import { hasAccess } from '@components/Authorize';
import { Formik, Form } from 'formik';
import {
  convLocalToIsoString,
  getUtcToLocal,
  isValueEmpty,
  isAvalancheSeason,
  getAvalancheSeason,
} from '@awp/helpers/utilsReact';
import { toast } from 'react-toastify';
import { downloadFile } from '@awp/utils/file-download';

import AwpYesNoModal from '@components/Common/AwpYesNoModal';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import CreateSupportingModal from '@awp/components/Avalanche/CreateSupportingModal';
import SupportAnalysisEdit from '@awp/components/Avalanche/SupportAnalysisEdit';
import SupportWeatherObservationEdit from '@awp/components/Avalanche/SupportWeatherObservationEdit';
import WeatherForecastAccordion from './SupportingWeatherForecast/EntityAccordion';
import ProcessedHazardForecastMetricTable from '@awp/components/Avalanche/ProcessedHazardForecastMetricTable';
import { useAvalancheEventContext } from './AvalanceEventContext';

function getHazardAreas(arr) {
  if (!arr || arr.length === 0) return arr;
  const result = [];
  for (const obj of arr) {
    const newObj = { ...obj, checked: false }; //added checked: attribute which is not supported by c#
    if (newObj.children && newObj.children.length) {
      newObj.children = getHazardAreas(newObj.children);
    }
    result.push(newObj);
  }
  return result;
}

const ProcessedHazardForecastEdit = ({
  setMode,
  mode,
  setEventId,
  eventId,
  setLoading,
  loading,
  fullData,
  setEventTypeToActOn,
  isRoute,
  isDuplicated,
  breadcrumbName,
}) => {
  const dispatch = useDispatch();

  const { fullContextData } = useAvalancheEventContext() ?? {};

  const loadedData = fullContextData ?? fullData;

  const currentUser = useSelector(selectCurrentUser);
  const weatherForecastExternalObjects = useSelector(currentWeatherForecastExternalObjects);
  const location = useLocation();
  const navigate = useNavigate();
  const { id: eventIdParam } = useParams();

  const avalanchePrograms = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheAreas0 = useSelector((state) => selectAllHazardForecastDropDown(state));
  const avalancheAreas = getHazardAreas(avalancheAreas0); //added [checked] attribute which is not supported by c#
  const avalancheTechs = useSelector((state) => selectHazardTechnicians(state));
  const levelDropDown = useSelector((state) => selectAllHazardForecastLevelDropDown(state));
  const selectedAnalysis = useSelector(currentAnalysis);
  const selectedWeatherForecast = useSelector(currentWeatherForecast);
  const selectedWeatherObservation = useSelector(currentWeatherObservation);

  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [wsForecastLoading, setWsForecastLoading] = useState(false);
  const [wsObservationLoading, setWsObservationLoading] = useState(false);
  const [refreshingWsObservation, setRefreshingWsObservation] = useState(false);
  const [isTransmitted, setIsTransmitted] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [disableOverride, setDisableOverride] = useState(false);
  const [analysisMode, setAnalysisMode] = useState(FORM_MODE.VIEW);
  const [forecastMode, setForecastMode] = useState(FORM_MODE.VIEW);
  const [observationMode, setObservationMode] = useState(FORM_MODE.VIEW);
  const activeAvalancheTechs =
    mode === FORM_MODE.CREATE ? avalancheTechs.options.filter((x) => x.isactive) : avalancheTechs;
  const defaultLevel = '1 - Low';
  const defaultDto = {
    program: null,
    area: {},
    override: false,
    forecaster: avalancheTechs.options?.find((t) => t.value === currentUser.SecurityIdentifierId) || null,
    forecastLevel: null,
    isRoadOpen: '',
    timestamp: new Date(),
    comment: '',
    //transmit: false,
    entityStatus: '',
    currentForecastLevel: defaultLevel,
    concurrencyControlNumber: null,
    lastUpdateUserId: '',
    lastUpdatetimestamp: null,
    createUserId: '',
    transmitOn: null,
  };

  const [dto, setDto] = useState(defaultDto);
  const [eventErrors, setEventErrors] = useState(null);
  const [isAvalancheTech, setIsAvalancheTech] = useState(true);
  const [areaList, setAreaList] = useState(avalancheAreas);
  const [createModal, setCreateModal] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [selectedForecastLevel, setSelectedForecastLevel] = useState('');
  const readOnly = mode !== FORM_MODE.EDIT && mode !== FORM_MODE.CREATE;
  const title =
    mode === FORM_MODE.EDIT
      ? 'Edit Hazard Forecast'
      : mode === FORM_MODE.CREATE
      ? 'Create Hazard Forecast'
      : 'Hazard Forecast';
  useEffect(() => {
    if (mode === FORM_MODE.CREATE) {
      dispatch(setAnalysis(null));
      dispatch(setWeatherObservation(null));
      dispatch(setWeatherForecastExternalObjects([]));
      applyUserPreferences();
    }
    if (mode === FORM_MODE.CREATE || mode === FORM_MODE.EDIT) {
      setIsAvalancheTech(avalancheTechs.options.findIndex((t) => t.value === currentUser.SecurityIdentifierId) !== -1);
    }
    if (mode !== FORM_MODE.CREATE) {
      setLoading(true);

      const url = REST_API.HAZARD_FORECAST + eventId;

      GetJson(url)
        .then((response) => response.json())
        .then((entity) => {
          dispatch(
            setWeatherObservation(null), //clean previous cache
          );
          dispatch(setWeatherForecastExternalObjects([]));
          setDto(entityToDto(entity));
          if (!isValueEmpty(entity.PublishedTimestamp)) {
            setMode(FORM_MODE.TRANSMIT);
            setAnalysisMode(FORM_MODE.TRANSMIT);
            setObservationMode(FORM_MODE.TRANSMIT);
          }
          if (!isValueEmpty(entity.ObservationSnapshotTimestamp)) {
            setWsObservationLoading(true);
            const urlWeatherObservation = REST_API.HAZARD_FORECAST_WEATHER_OBSERVATION + eventId;
            GetJson(urlWeatherObservation)
              .then((response) => response.json())
              .then((entityWeatherObservation) => {
                if (entityWeatherObservation) dispatch(setWeatherObservation(entityWeatherObservation));
              })
              .finally(() => {
                setWsObservationLoading(false);
              });
          }
        })
        .finally(() => {
          setLoading(false);
        });

      setAnalysisLoading(true);

      const urlAnalysis = REST_API.HAZARD_FORECAST_ANALYSIS + eventId;
      dispatch(
        setAnalysis(null), //clean previous cache
      );
      GetJson(urlAnalysis)
        .then((response) => response.json())
        .then((entityAnalysis) => {
          if (
            entityAnalysis &&
            (entityAnalysis.HazardForecastComment ||
              entityAnalysis.WeatherComment ||
              entityAnalysis.SnowpackDescription ||
              entityAnalysis.AvalancheObservationSummary ||
              entityAnalysis.AvalancheActivityForecast ||
              entityAnalysis.HazardTrend)
          )
            dispatch(setAnalysis(entityAnalysis));
        })
        .finally(() => {
          setAnalysisLoading(false);
        });
    }
  }, []);

  const filterObjectsByParentId = (data, programId) => {
    const filteredList = data.filter((item) => {
      if (item.treeLevel === 1 && item.parentId !== programId) {
        return false;
      }
      if (item.children && item.children.length > 0) {
        item.children = filterObjectsByParentId(item.children, programId);
      }
      return true;
    });

    return filteredList;
  };
  const findFirstObjectByValue = (array, key, value) => {
    for (let i = 0; i < array.length; i++) {
      const obj = array[i];
      if (obj[key] === value) {
        return obj;
      }
      if (obj.children && obj.children.length > 0) {
        const match = findFirstObjectByValue(obj.children, key, value);
        if (match) {
          return match;
        }
      }
    }
    return null;
  };

  const applyUserPreferences = () => {
    const data = (JSON.parse(currentUser.Preferences) ?? {})?.hforecast;

    if (!data || !data.program) return;

    let area = null;
    let selectedArea = [];
    let program = avalanchePrograms.find((x) => x.value === data.program && !x.isDisabled);

    if (!program) return;

    if (data.area) {
      area = findFirstObjectByValue(avalancheAreas, 'value', data.area);
      if (!area.disabled) {
        selectedArea.push(area);
        program = getProgramByArea(area);
      }
    }

    const dtoTemp = {
      ...defaultDto,
      program,
      area: selectedArea,
    };
    const _areaList = filterObjectsByParentId(avalancheAreas, data.program);
    setAreaList(_areaList);
    updateHazardAreaList(selectedArea, _areaList); //must be after setArealist
    setDto(dtoTemp);
    if (selectedArea && selectedArea[0] && selectedArea[0].groupName === 'Forecast Areas') setDisableOverride(true);
  };

  const programOnChange = (opt, setFieldValue) => {
    setFieldValue('area', null, false);
    setAreaList(filterObjectsByParentId(avalancheAreas, opt.value));
  };
  const getProgramByArea = (area) => {
    if (!area) return null;
    if (area.treeLevel < 2) return avalanchePrograms.find((x) => x.value === area.parentId);
    const areaParent = findFirstObjectByValue(avalancheAreas, 'value', area.parentId);
    if (areaParent) return avalanchePrograms.find((x) => x.value === areaParent.parentId);
    return null;
  };
  const entityToDto = (entity) => {
    const area = findFirstObjectByValue(avalancheAreas, 'value', entity.Event.GeographicAreaId);
    let selectedArea = [];
    selectedArea.push(area);
    const dtoTemp = {
      program: getProgramByArea(area),
      area: selectedArea,
      timestamp: new Date(getUtcToLocal(entity.Event.EventTimestamp)),
      comment: entity.Event.NoteComment,
      notification: entity.Notification,
      //transmit: entity.IsProtected === "Y" ? true : false,
      override: entity.IsOverride === 'Y' ? true : false,
      isRoadOpen: isValueEmpty(entity.IsRoadOpen) ? '' : entity.IsRoadOpen,
      entityStatus: entity.Event.EntityStatusType.StatusValue,
      eventId: entity.EventId,
      eventTypeId: entity.Event.EventTypeId,
      forecaster: avalancheTechs.options.find((t) => t.value === entity.ForecasterId) || null,
      forecastLevel:
        levelDropDown.find(
          (l) =>
            entity.ForecastLevel &&
            l.value === entity.ForecastLevel.DetailText &&
            l.eventTypePropertyId === entity.ForecastLevel.EventTypePropertyId,
        ) || null,
      currentForecastLevel: !isValueEmpty(entity.CurrentForecastLevelValue)
        ? entity.CurrentForecastLevelValue
        : defaultLevel,
      concurrencyControlNumber: entity.ConcurrencyControlNumber,
      lastUpdateUserId: entity.LastUpdateUserId ? (entity.LastUpdateUserId + '').toUpperCase() : '',
      lastUpdatetimestamp: entity.LastUpdatetimestamp ? getUtcToLocal(entity.LastUpdatetimestamp + '') : '',
      createUserId: entity.CreateUserId ? (entity.CreateUserId + '').toUpperCase() : '',
      transmitOn: entity.PublishedTimestamp ? getUtcToLocal(entity.PublishedTimestamp + '') : '',
      notificationRecipientMetricStat:
        entity.NotificationRecipientMetricStat &&
        entity.NotificationRecipientMetricStat.NotificationRecipientMetrics &&
        entity.NotificationRecipientMetricStat.NotificationRecipientMetrics.length > 0
          ? entity.NotificationRecipientMetricStat
          : null,
    };
    if (!isValueEmpty(dtoTemp.transmitOn)) {
      setIsTransmitted(true);
      setMode(FORM_MODE.TRANSMIT);
      setAnalysisMode(FORM_MODE.TRANSMIT);
      setObservationMode(FORM_MODE.TRANSMIT);
    }
    let _areaList = filterObjectsByParentId(avalancheAreas, dtoTemp.program.value);
    dtoTemp.program && setAreaList(_areaList);
    updateHazardAreaList(selectedArea, _areaList); //must be after setArealist
    setSelectedForecastLevel(dtoTemp.forecastLevel ? dtoTemp.forecastLevel.label : '');
    if (dtoTemp && dtoTemp.area && dtoTemp.area[0] && dtoTemp.area[0].groupName === 'Forecast Areas')
      setDisableOverride(true);
    return dtoTemp;
  };

  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);
    const ts = convLocalToIsoString(values.timestamp ?? new Date());
    const entity = {
      //IsProtected: values.transmit ? "Y" : "N",
      IsOverride: values.override ? 'Y' : 'N',
      Forecaster: values.forecaster,
      IsStdWeatherObsOnly: 'N',
      IsRoadOpen: values.isRoadOpen,
      Notification: values.notification,
      Event: {
        EventTimestamp: ts,
        EventTimestampErrorMinutes: 0,
        IsIncident: 'N',
        NoteComment: values.comment,
        GeographicAreaId: values.area && values.area[0] ? values.area[0].value : null,
        ParentGeographicAreaId: values.area && values.area[0] ? values.area[0].parentId : null,
        //ConcurrencyControlNumber: dto.concurrencyControlNumber,
      },
      ForecastLevel: {
        EventTypePropertyId: values.forecastLevel ? values.forecastLevel.eventTypePropertyId : null,
        DetailText: values.forecastLevel ? values.forecastLevel.value : '',
        //ObservationTypeId: values.forecastLevel ? values.forecastLevel.observationTypeId : null,
      },
    };

    const hzEntity = {
      ...entity,
      EventId: dto.eventId,
    };
    hzEntity.Event.EventId = dto.eventId;
    hzEntity.Event.EventTypeId = dto.eventTypeId;

    if (isPreview) {
      setSubmitting(true);
      previewHazardForecast(hzEntity, () => {
        setSubmitting(false);
        setIsPreview(false);
      });
      return;
    }

    if (mode === FORM_MODE.EDIT) {
      if (!isTransmitted) {
        updateHazardForecast(setSubmitting, resetForm, setErrors, hzEntity);
      } else {
        transmitHazardForecast(setSubmitting, resetForm, setErrors, hzEntity);
      }
    } else {
      dispatch(
        setAnalysis(null), //clean previous cache
      );
      dispatch(setWeatherObservation(null));
      createHazardForecast(setSubmitting, setErrors, entity);
    }
  };

  const updateHazardForecast = (setSubmitting, resetForm, setErrors, hzDto) => {
    setSubmitting(true);
    const url = REST_API.HAZARD_FORECAST + hzDto.EventId;

    PutJson(url, hzDto)
      .then(async (response) => {
        if (response.ok) {
          toast.success('Hazard forecast has been updated');
          const entity = await response.json();
          const dtoTemp = entityToDto(entity);
          setDto(dtoTemp);
          resetForm();
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEventErrors({ Entity: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const transmitHazardForecast = (setSubmitting, resetForm, setErrors, hzDto) => {
    setSubmitting(true);
    const url = REST_API.HAZARD_FORECAST + 'transmit/' + hzDto.EventId;

    PutJson(url, hzDto)
      .then(async (response) => {
        if (response.ok) {
          toast.success('Hazard forecast has been transmitted');
          const entity = await response.json();
          const dtoTemp = entityToDto(entity);
          setDto(dtoTemp);
          if (!isValueEmpty(entity.PublishedTimestamp)) {
            setIsTransmitted(true);
            setMode(FORM_MODE.TRANSMIT);
            setAnalysisMode(FORM_MODE.TRANSMIT);
            setObservationMode(FORM_MODE.TRANSMIT);
          }
          resetForm();
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEventErrors({ Entity: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const previewHazardForecast = async (hzDto, callback) => {
    const url = 'files/hazardforecast/download';

    const response = await PostJson(url, hzDto);
    if (response.ok) {
      const blob = await response.blob();
      downloadFile(blob, 'download.pdf');
    } else {
      toast.error('Failed to download the PDF');
    }

    callback();
  };

  const createHazardForecast = (setSubmitting, setErrors, hzDto) => {
    const url = REST_API.HAZARD_FORECAST;
    PostJson(url, hzDto)
      .then(async (response) => {
        if (response.ok) {
          const entity = await response.json();
          const dtoTemp = entityToDto(entity);
          setDto(dtoTemp);
          setEventId(dtoTemp.eventId);
          toast.success('Hazard forecast has been created');
          setMode(FORM_MODE.EDIT);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEventErrors({ Entity: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const handleBtnClose = (props) => {
    props.resetForm();
    if (mode === FORM_MODE.CREATE) {
      navigate(location.pathname);
    }

    setMode(FORM_MODE.LIST);
    if (eventIdParam) {
      // Check if deep linking is being used
      navigate(Views.AvalancheProcessedEvent);
    }
  };
  const forecastLevelOnChange = (opt, values, setFieldValue) => {
    if (['1', '2', '3'].includes(opt.value)) {
      setFieldValue('isRoadOpen', 'Y', false);
    } else if (['4', '5'].includes(opt.value)) {
      setFieldValue('isRoadOpen', 'N', false);
    }
  };

  const handleModalClosed = useCallback(() => {
    setCreateModal(false);
  }, []);
  const clickSaveTransmit = (props) => {
    setShowSaveConfirm(true);
  };
  const getForecastWeatherObs = (from, dto) => {
    if (from === 'modal') setWsObservationLoading(true);
    const apiStartDate = dto.startTimestamp ? formatDate(dto.startTimestamp, 'yyyy-MM-dd-HH-mm-ss') : '-1';
    const apiEndDate = formatDate(dto.endTimestamp, 'yyyy-MM-dd-HH-mm-ss');
    const isStandard = dto.isStandard ? 'Y' : 'N';
    const urlWeatherObservation = `${REST_API.HAZARD_FORECAST_RAW_WEATHER_OBSERVATION}${eventId}/${apiStartDate}/${apiEndDate}/${isStandard}`;
    if (from === 'modal') {
      dispatch(setWeatherObservation(null));
    }
    GetJson(urlWeatherObservation)
      .then((response) => response.json())
      .then((entityWeatherObservation) => {
        if (entityWeatherObservation) {
          dispatch(setWeatherObservation(entityWeatherObservation));
        } else {
          dispatch(setWeatherObservation(null));
        }
      })
      .finally(() => {
        if (from === 'modal') setWsObservationLoading(false);
        if (from === 'refresh') setRefreshingWsObservation(false);
      });
  };

  const breadCrumb = () => {
    const entity = loadedData?.find((x) => x.id === eventId);
    const name = entity?.internal.avalanche_program?.label;
    return name ? name : eventId;
  };

  const Buttons = ({ props }) => {
    const previewBtn = (
      <Button
        disabled={!props.isValid || props.isSubmitting}
        className="ml-2"
        variant="outline-primary"
        onClick={() => {
          setIsPreview(true);
          if (props) props.submitForm();
        }}
      >
        {props.isSubmitting && isPreview ? (
          <>
            <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
            Generating...
          </>
        ) : (
          <span>{mode === FORM_MODE.TRANSMIT ? 'View PDF' : 'Preview'}</span>
        )}
      </Button>
    );

    return mode === FORM_MODE.EDIT || mode === FORM_MODE.CREATE ? (
      <>
        <div className="mt-2 mr-3" style={{ float: 'left', marginTop: '1.25rem' }}>
          {!showCancelConfirm && (
            <Button
              className="mr-2"
              variant="outline-primary"
              onClick={() => {
                setShowCancelConfirm(true);
              }}
            >
              {'Cancel'}
            </Button>
          )}

          {!showCancelConfirm && hasAccess([Permissions.WriteAvalancheHazard]) && (
            <>
              <Button type="submit" variant="primary" disabled={!props.isValid || !props.dirty || props.isSubmitting}>
                {props.isSubmitting && !isPreview ? (
                  <>
                    <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  <span>Save</span>
                )}
              </Button>
              {mode === FORM_MODE.EDIT && (
                <Button
                  disabled={!props.isValid || props.dirty || props.isSubmitting}
                  className="ml-2"
                  variant="primary"
                  onClick={() => setCreateModal(!createModal)}
                >
                  Add Supporting Data
                </Button>
              )}
              <Button
                disabled={!props.isValid || props.dirty || props.isSubmitting}
                className="ml-2"
                variant="outline-primary"
                onClick={() => {
                  clickSaveTransmit(props);
                }}
              >
                {'Transmit'}
              </Button>
              {previewBtn}
            </>
          )}
          {showCancelConfirm && (
            <AwpYesNoDialog
              action={() => handleBtnClose(props)}
              setShowYesNoConfirm={setShowCancelConfirm}
              msg={'Are you sure you want to cancel?'}
              props={{}}
            />
          )}
        </div>
      </>
    ) : (
      <div style={{ marginTop: '1.25rem' }}>
        <Button
          className="mr-2"
          variant="outline-primary"
          onClick={() => {
            handleBtnClose(props);
          }}
        >
          {'Close'}
        </Button>
        {previewBtn}
      </div>
    );
  };

  Buttons.propTypes = {
    title: PropTypes.string,
    props: PropTypes.any,
    resetForm: PropTypes.func,
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    dirty: PropTypes.bool,
  };

  const updateAllCheckedField = (arr, isChecked) => {
    arr.forEach((obj) => {
      obj.checked = isChecked; // update the checked field of each object
      if (obj.children) {
        // if the object has children, recursively call the function on the children
        updateAllCheckedField(obj.children, isChecked);
      }
    });
  };

  const updateAreaChecked = (ids, arr) => {
    let found = false;

    function update(arr) {
      for (let i = 0; i < arr.length; i++) {
        if (ids.includes(arr[i].value)) {
          arr[i].checked = true;
          found = true;
        }
        if (arr[i].children && arr[i].children.length > 0) {
          update(arr[i].children);
        }
      }
    }

    update(arr);

    if (found) {
      return arr;
    } else {
      return [...arr];
    }
  };

  const updateHazardAreaList = (selectedOptions, _areaList) => {
    //let _areaList = [...areaList];
    let ids = [];
    if (selectedOptions && selectedOptions.length > 0) {
      selectedOptions.map((r) => {
        if (r && r.value > 0) ids.push(r.value);
      });
    }

    updateAllCheckedField(_areaList, false); //clean old selected
    if (!ids || ids.length === 0) {
      setAreaList(avalancheAreas); //reset all unselected
      return;
    }
    updateAreaChecked(ids, _areaList);
    setAreaList(_areaList);
  };

  const onHazardAreaChange = (currentNode, selectedNodes, setFieldValue) => {
    let _selectedNodes = selectedNodes && selectedNodes.length > 0 ? selectedNodes : null;
    setTimeout(() => setFieldValue('area', _selectedNodes));
    if (_selectedNodes && _selectedNodes[0] && _selectedNodes[0].groupName === 'Forecast Areas') {
      setFieldValue('override', false);
      setDisableOverride(true);
    } else {
      setDisableOverride(false);
    }
    if (_selectedNodes && _selectedNodes[0] && _selectedNodes[0].parentId > 0) {
      let program = getProgramByArea(_selectedNodes[0]);
      setFieldValue(
        'program',
        program,
        //avalanchePrograms.find((x) => x.value === _selectedNodes[0].parentId)
      );
      let _areaList = filterObjectsByParentId(avalancheAreas, program.value);
      setAreaList(_areaList);
      updateHazardAreaList(_selectedNodes, _areaList);
    } else {
      let _areaList = filterObjectsByParentId(avalancheAreas, currentNode.parentId);
      setAreaList(_areaList);
      updateHazardAreaList(_selectedNodes, avalancheAreas);
    }
  };

  const handleSaveTransmit = (props) => {
    setIsTransmitted(true);
    if (props) props.submitForm();
  };
  return (
    <>
      {loading || analysisLoading ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : (
        <>
          <span
            className="awp-text-btn"
            onClick={() => {
              setMode(FORM_MODE.LIST);
              if (eventIdParam) {
                // Check if deep linking is being used
                navigate(Views.AvalancheProcessedEvent);
              }
            }}
          >
            {breadcrumbName}
          </span>
          <span className="mx-3">&gt;</span>
          <span>{breadCrumb()}</span>
          <Formik
            enableReinitialize
            validateOnMount
            validateOnBlur
            validateOnChange
            initialValues={dto}
            initialTouched={{
              area: false,
              forecastLevel: false,
              timestamp: true,
              isRoadOpen: false,
            }}
            validationSchema={Yup.object({
              comment: Yup.string().optional().label('Comment').max(2000, '${label} must not exceed 2,000 characters'),
              notification: Yup.string()
                .optional()
                .label('Notification')
                .max(100, '${label} must not exceed 100 characters'),
              timestamp: Yup.date()
                .nullable()
                .required('Effective Date Time is required')
                .test(
                  'timestamp',
                  'Effective Date Time must be within the current Avalanche season',
                  (value, context) => {
                    return isAvalancheSeason(value);
                  },
                ),
              area: Yup.object()
                .nullable()
                .test('area', 'Area is required', (value, context) => {
                  return (
                    (!isValueEmpty(value) && !isValueEmpty(value.value)) ||
                    (context && context.originalValue && context.originalValue.length > 0)
                  );
                }),
              forecastLevel: Yup.object()
                .nullable()
                .test('forecastLevel', 'Forecast level is required', (value, context) => {
                  if (value && !isValueEmpty(value.value)) return true;
                  return false;
                }),
              isRoadOpen: Yup.string().label('Road Status').required('Road status is required'),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <>
                <div className="d-flex">
                  <h1 className="flex-grow-1" style={{ paddingTop: '10px' }}>
                    {title}
                  </h1>
                </div>
                <Form>
                  <Card
                    border="light"
                    className="page-card"
                    style={{ border: '0px', boxShadow: '0px 0px 0px #000000' }}
                  >
                    <Card.Body className="pr-pl-pt-0">
                      {(mode === FORM_MODE.EDIT || mode === FORM_MODE.VIEW || mode === FORM_MODE.TRANSMIT) && dto && (
                        <>
                          <FormTextReadOnly label="Created By" textValue={dto.createUserId} />
                          <FormTextReadOnly label="Last Updated" textValue={dto.lastUpdatetimestamp} />
                          <FormTextReadOnly label="Last Updated By" textValue={dto.lastUpdateUserId} />
                          <FormTextReadOnly label="Transmitted" textValue={dto.transmitOn ? dto.transmitOn : ''} />
                        </>
                      )}
                      <FormSelect
                        className="avalanche-select"
                        readOnly={
                          readOnly || wsObservationLoading || refreshingWsObservation || selectedWeatherObservation
                        }
                        name="program"
                        id="program"
                        label="Program"
                        options={avalanchePrograms}
                        isMulti={false}
                        postChange={(e) => {
                          programOnChange(e, props.setFieldValue);
                        }}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 1001 }) }}
                      />

                      <FormTreeSelect
                        className="show-selected-item-yes"
                        readOnly={
                          readOnly || wsObservationLoading || refreshingWsObservation || selectedWeatherObservation
                        }
                        name="area"
                        id="area"
                        label="Area (required)"
                        options={areaList}
                        onChange={(currentNode, selectedNodes) => {
                          onHazardAreaChange(currentNode, selectedNodes, props.setFieldValue);
                        }}
                      />
                      <FormForecastLevel
                        readOnly={true}
                        name="currentForecastLevel"
                        id="currentForecastLevel"
                        label="Override (excludes this Area from any change unless explicitly made for the Area)"
                      />
                      {!readOnly && (
                        <OverlayTrigger
                          placement="top-start"
                          overlay={
                            <Tooltip>
                              {
                                'The Hazard Forecast will not change until a new Hazard Forecast is transmitted for this specific Area'
                              }
                            </Tooltip>
                          }
                        >
                          <FormCheckbox
                            readOnly={readOnly || disableOverride}
                            id="override"
                            name="override"
                            label="Override"
                            disabled={disableOverride}
                          ></FormCheckbox>
                        </OverlayTrigger>
                      )}
                      {readOnly && (
                        <FormCheckbox
                          readOnly={readOnly}
                          id="override"
                          name="override"
                          label="Override"
                          disabled={disableOverride}
                        />
                      )}
                      <FormSelect
                        readOnly={readOnly}
                        name="forecaster"
                        id="forecaster"
                        label="Forecaster (required)"
                        options={mode === FORM_MODE.CREATE ? activeAvalancheTechs : avalancheTechs.options}
                        isMulti={false}
                      />
                      <div className="customWidthDatePicker">
                        <FormDatePicker
                          readOnly={readOnly}
                          label="Effective Date Time (required)"
                          id="timestamp"
                          name="timestamp"
                          showTimeSelect={true}
                          showTime={{ isEnabled: true }}
                          timeFormat="HH:mm"
                          timeIntervals={30}
                          dateFormat={['yyyy-MM-dd HH:mm']}
                        />
                      </div>
                      <FormSelect
                        readOnly={readOnly}
                        name="forecastLevel"
                        id="forecastLevel"
                        label="Forecast Level (required)"
                        options={levelDropDown?.filter((l) => l.isActive)}
                        postChange={(e) => {
                          forecastLevelOnChange(e, props.values, props.setFieldValue);
                        }}
                        isMulti={false}
                        focus={isDuplicated && mode === FORM_MODE.EDIT}
                      />

                      <FormRadioGroup
                        label="Road Status (required)"
                        name="isRoadOpen"
                        id="isRoadOpen"
                        readOnly={readOnly}
                        textValue={
                          props.values.isRoadOpen + '' === 'N'
                            ? 'Closed'
                            : props.values.isRoadOpen + '' === 'Y'
                            ? 'Open'
                            : ''
                        }
                      >
                        <Row>
                          <Col sm={1} md={1} lg={1}>
                            <FormRadio
                              id="isRoadOpenYes"
                              label="Open"
                              name="isRoadOpen"
                              value="Y"
                              readOnly={
                                readOnly
                                  ? true
                                  : !props.values.forecastLevel
                                  ? false
                                  : ['4', '5'].includes(props.values.forecastLevel.value)
                              }
                            />
                          </Col>
                          <Col sm={1} md={1} lg={1}>
                            <FormRadio
                              id="isRoadOpenNo"
                              label="Closed"
                              name="isRoadOpen"
                              value="N"
                              readOnly={readOnly}
                            />
                          </Col>
                        </Row>
                      </FormRadioGroup>
                      <FormTextArea readOnly={readOnly} label="Comment" id="comment" name="comment" rows={3} />
                      <FormTextArea
                        readOnly={readOnly}
                        label="Notification"
                        id="notification"
                        name="notification"
                        rows={3}
                      />

                      <div>
                        <b>Form Status:</b> <span>{dto.entityStatus}</span>
                      </div>

                      <div>
                        <Buttons title=" " props={props} />
                      </div>
                      <br />
                    </Card.Body>
                  </Card>
                  <AwpYesNoModal
                    showYesNoModal={showSaveConfirm}
                    setShowYesNoModal={setShowSaveConfirm}
                    onClickYes={() => {
                      setShowSaveConfirm(false);
                      handleSaveTransmit(props);
                    }}
                    title="Transmit"
                    body={
                      <>
                        {'The Hazard Forecast Level to be transmitted is '}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Button
                            className={`forecast-level-${
                              isValueEmpty(selectedForecastLevel) ? '1' : selectedForecastLevel.charAt(0)
                            } forecast-level-selected`}
                            style={{ marginLeft: '8px' }}
                          >
                            {selectedForecastLevel}
                          </Button>
                          <div style={{ display: 'flex', marginLeft: '8px' }}>
                            {dto?.area?.length > 0 ? (
                              dto.area.map((area, index) => (
                                <div key={index} style={{ marginRight: '8px' }}>
                                  <span>{area.label}</span>
                                </div>
                              ))
                            ) : (
                              <div>No areas selected</div>
                            )}
                          </div>
                        </div>
                        <br />
                        {'Please note that the action cannot be reversed or undone. Do you want to continue?'}
                      </>
                    }
                  />
                </Form>
              </>
            )}
          </Formik>
          {wsObservationLoading && (
            <Container>
              <Spinner animation="border" />
            </Container>
          )}
          {!wsObservationLoading && selectedWeatherObservation && (
            <Accordion defaultActiveKey="0">
              <Card border="light" className="page-card">
                <CardHeaderToggle eventKey="0" as="h3" title={'Weather Observations'} />
                <Accordion.Collapse eventKey="0">
                  <Card.Body>
                    <div>
                      <SupportWeatherObservationEdit
                        eventId={dto.eventId}
                        mode={observationMode}
                        setMode={setObservationMode}
                        getForecastWeatherObs={getForecastWeatherObs}
                        wsObservationLoading={wsObservationLoading}
                        setWsObservationLoading={setWsObservationLoading}
                        refreshingWsObservation={refreshingWsObservation}
                        setRefreshingWsObservation={setRefreshingWsObservation}
                      />
                    </div>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          )}

          {!createModal && <WeatherForecastAccordion eventId={eventId} isTransmitted={isTransmitted} />}
          {selectedAnalysis && (
            <Accordion defaultActiveKey="0">
              <Card border="light" className="page-card">
                <CardHeaderToggle eventKey="0" as="h3" title={'Analysis'} />
                <Accordion.Collapse eventKey="0">
                  <Card.Body>
                    <div>
                      <SupportAnalysisEdit eventId={dto.eventId} mode={analysisMode} setMode={setAnalysisMode} />
                    </div>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          )}
          {dto.notificationRecipientMetricStat && (
            <Accordion defaultActiveKey="0">
              <Card border="light" className="page-card">
                <CardHeaderToggle eventKey="0" as="h3" title={'Distribution Metrics'} />
                <Accordion.Collapse eventKey="0">
                  <Card.Body>
                    <div>
                      <ProcessedHazardForecastMetricTable
                        eventId={eventId}
                        data={dto.notificationRecipientMetricStat}
                      />
                    </div>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          )}
          <AwpValidationErrorModal setErrors={setEventErrors} errors={eventErrors} />

          <CreateSupportingModal
            visible={createModal}
            closeModal={handleModalClosed}
            analysisMode={analysisMode}
            observationMode={observationMode}
            setForecastMode={setForecastMode}
            setAnalysisMode={setAnalysisMode}
            eventId={dto.eventId}
            setObservationMode={setObservationMode}
            setWsObservationLoading={setWsObservationLoading}
            getForecastWeatherObs={getForecastWeatherObs}
          />
        </>
      )}
    </>
  );
};

ProcessedHazardForecastEdit.propTypes = {
  setMode: PropTypes.func,
  mode: PropTypes.string,
  setEventId: PropTypes.func,
  eventId: PropTypes.any,
  setLoading: PropTypes.func,
  loading: PropTypes.bool,
  setFieldValue: PropTypes.func,
  validateField: PropTypes.func,
  setFieldTouched: PropTypes.func,
  values: PropTypes.any,
  resetForm: PropTypes.func,
  fullData: PropTypes.array,
  setEventTypeToActOn: PropTypes.func,
  isDuplicated: PropTypes.bool,
};

export default ProcessedHazardForecastEdit;
